import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import { abortUnverifiedAuthSession } from './abortUnverifiedAuthSession';
import type { AuthSheetState } from './authSheetContext';
import { otpChromeDismissBlocked, resolveOtpDismissPolicy } from './otpSheetPolicy';
import { useAuthStore } from './store';
import {
  exitOrGoBackViaService,
  resetAuthenticatedHome,
  type NavigationHandle,
} from '../navigation/navigationService';

function otpParamsFromState(sheet_state: AuthSheetState | null): Record<string, unknown> | null {
  if (sheet_state?.kind !== 'otp') {
    return null;
  }
  return sheet_state.params;
}

export function useAuthSheetOtpChrome(
  state: AuthSheetState | null,
  displayed: AuthSheetState | null,
  contextDismiss: () => void,
) {
  const navigation = useNavigation();
  const is_authenticated = useAuthStore(s => s.isAuthenticated);

  const active_otp_params = otpParamsFromState(displayed) ?? otpParamsFromState(state);
  const otp_dismiss_blocked = active_otp_params != null && otpChromeDismissBlocked(active_otp_params);
  const show_otp_close_button = active_otp_params != null && resolveOtpDismissPolicy(active_otp_params) !== 'blocking';

  const dismissFromChrome = useCallback(() => {
    if (state?.kind === 'otp') {
      const policy = resolveOtpDismissPolicy(state.params);
      const nav = navigation as unknown as NavigationHandle;
      if (policy === 'blocking') {
        return;
      }
      if (policy === 'gated') {
        exitOrGoBackViaService(nav, is_authenticated);
        contextDismiss();
        return;
      }
      if (policy === 'allow_exit') {
        resetAuthenticatedHome(nav);
        contextDismiss();
        return;
      }
    }
    contextDismiss();
  }, [state, contextDismiss, navigation, is_authenticated]);

  const onOverlayRequestClose = useCallback(() => {
    if (otp_dismiss_blocked) {
      return;
    }
    dismissFromChrome();
  }, [otp_dismiss_blocked, dismissFromChrome]);

  const handleOtpAbortSafeExit = useCallback(() => {
    const nav = navigation as unknown as NavigationHandle;
    contextDismiss();
    void abortUnverifiedAuthSession(nav);
  }, [contextDismiss, navigation]);

  return {
    dismissFromChrome,
    onOverlayRequestClose,
    otp_dismiss_blocked,
    show_otp_close_button,
    handleOtpAbortSafeExit,
  };
}
